import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSupabaseAuth } from "@/hooks/useSupabaseAuth";
import Logo from "./Logo";
import AuthModal from "./AuthModal";
import LanguageSelector from "./header/LanguageSelector";
import ThemeToggle from "./header/ThemeToggle";
import MobileMenu from "./header/MobileMenu";
import MobileNavButton from "./header/MobileNavButton";
import DesktopNavigation from "./header/DesktopNavigation";
import UserInfo from "./header/UserInfo";
import FindPlacesButton from "./header/FindPlacesButton";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";

const WELCOME_TOAST_COOLDOWN = 60 * 60 * 1000; // 1 hour in ms
const WELCOME_TOAST_STORAGE_KEY = 'lastWelcomeToastTime';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<'signin' | 'signup'>('signin');
  const { user, isAdmin, isLoading } = useSupabaseAuth();
  const { toast: shadcnToast } = useToast();
  const isMobile = useIsMobile();
  const { t } = useLanguage();
  
  // Handle welcome toast only on actual SIGNED_IN events (not session restores)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        const lastShown = localStorage.getItem(WELCOME_TOAST_STORAGE_KEY);
        const lastShownTime = lastShown ? parseInt(lastShown, 10) : 0;
        const now = Date.now();
        
        // Only show toast if it hasn't been shown in the last hour
        if (now - lastShownTime > WELCOME_TOAST_COOLDOWN) {
          toast.success(t('auth.signedInSuccessfully'), {
            description: `${t('auth.welcome')} ${session.user.email}!`,
            action: {
              label: t('auth.dismiss'),
              onClick: () => {}
            },
            duration: 5000
          });
          localStorage.setItem(WELCOME_TOAST_STORAGE_KEY, now.toString());
        }
      }
    });
    
    return () => subscription.unsubscribe();
  }, [t]);

  const openAuthModal = (tab: 'signin' | 'signup') => {
    setAuthModalTab(tab);
    setAuthModalOpen(true);
  };

  const handleAuthSuccess = () => {
    setAuthModalOpen(false);
  };

  const handleAuthError = (message: string) => {
    console.error("Auth error:", message);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/90 backdrop-blur-sm">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <div className="flex items-center">
          <Link 
            to="/" 
            className="mr-6"
            onClick={(e) => {
              // Only refresh when on main page
              if (window.location.pathname === '/') {
                e.preventDefault();
                window.location.reload();
              }
            }}
          >
            <Logo />
          </Link>
        </div>

        {/* Desktop Navigation - moved closer to logo */}
        <div className="hidden md:flex md:flex-1 md:justify-start md:ml-2 lg:ml-4 md:mr-4 lg:mr-8">
          <DesktopNavigation />
        </div>

        <div className="flex items-center gap-1.5 md:gap-2 lg:gap-4">
          {/* FindPlaces button and Language Selector */}
          <FindPlacesButton isMobile={isMobile} />
          <LanguageSelector />

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Login/Signup - Desktop */}
          <UserInfo 
            user={user} 
            isAdmin={isAdmin}
            onSignIn={() => openAuthModal('signin')} 
            onSignUp={() => openAuthModal('signup')}
            isLoading={isLoading}
          />

          {/* Mobile menu button */}
          <MobileNavButton 
            isOpen={mobileMenuOpen} 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
          />
        </div>
      </div>

      {/* Mobile Navigation - Now expands within header */}
      <div className="md:hidden">
        <MobileMenu 
          user={user}
          isOpen={mobileMenuOpen} 
          onClose={() => setMobileMenuOpen(false)}
          onAuthModalOpen={openAuthModal}
        />
      </div>
      
      <AuthModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)}
        defaultTab={authModalTab}
        onSuccess={handleAuthSuccess}
        onError={handleAuthError}
      />
    </header>
  );
}
